import styles from "./ReasonToBuy.module.scss";
import { ReactNode, useRef, useState, useEffect } from "react";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

const MD_SCROLL_MEDIA_QUERY = "(min-width: 600px) and (max-width: 1023px)";
const LG_SCROLL_MEDIA_QUERY = "(min-width: 1024px)";

type Card = {
  id: string;
  title: string;
  description?: string | undefined;
  descriptionHref?: string | undefined;
  onDescriptionClick?: () => void;
  icon?: ReactNode;
};

type Props = {
  cards: Card[];
};

export const ReasonToBuyUI = ({ cards }: Props) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(1);

  useEffect(
    function listenToMediaQueryChanges() {
      const mqMd = window.matchMedia(MD_SCROLL_MEDIA_QUERY);
      const mqLg = window.matchMedia(LG_SCROLL_MEDIA_QUERY);

      const update = () => {
        if (mqLg.matches) setCardsPerPage(4);
        else if (mqMd.matches) setCardsPerPage(2);
        else setCardsPerPage(1);
      };

      update();

      [mqMd, mqLg].forEach((mq) => {
        mq.addEventListener("change", update);
      });
      return () => {
        [mqMd, mqLg].forEach((mq) => {
          mq.removeEventListener("change", update);
        });
      };
    },
    [cardsPerPage],
  );

  useEffect(
    function resetPagination() {
      const track = trackRef.current;
      if (!track) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = Number(
                entry.target.getAttribute("data-card-index"),
              );
              // Update active index based on the first fully visible card
              setActiveIndex(index);
            }
          });
        },
        {
          root: track,
          // threshold: 0.6 means the card must be at least 60% visible
          // to be considered the 'active' one.
          threshold: 0.6,
        },
      );

      const items = track.querySelectorAll("[data-card-index]");
      items.forEach((item) => observer.observe(item));

      return () => observer.disconnect();
    },
    [cards.length],
  );

  useEffect(
    function syncActiveIndexWithScroll() {
      const track = trackRef.current;
      if (!track) return;

      const handleScroll = () => {
        const totalPages = Math.ceil(cards.length / cardsPerPage);
        const pageWidth = track.scrollWidth / totalPages;
        const pageIndex = Math.round(track.scrollLeft / pageWidth);
        const index = Math.min(pageIndex, totalPages - 1) * cardsPerPage;
        setActiveIndex(Math.min(index, cards.length - 1));
      };

      track.addEventListener("scroll", handleScroll, { passive: true });
      return () => track.removeEventListener("scroll", handleScroll);
    },
    [cards.length, cardsPerPage],
  );

  if (!cards.length) {
    return null;
  }

  const totalPages = Math.ceil(cards.length / cardsPerPage);
  const activePage = Math.floor(activeIndex / cardsPerPage);

  return (
    <div data-component="ReasonToBuy" className={styles.container}>
      <ul
        className={styles.cards}
        data-testid="reason-to-buy-cards-track"
        ref={trackRef}
      >
        {cards.map((card) => (
          <li
            key={card.id}
            className={cx(styles.card, {
              "card--withOneView": totalPages === 1,
            })}
            data-testid={`reason-to-buy-card-${card.id}`}
          >
            <div className={styles.cardHeader}>
              {card.icon && (
                <span className={styles.cardIcon}>{card.icon}</span>
              )}
            </div>
            <p className={styles.cardTitle}>{card.title}</p>
            {card.description &&
              (card.descriptionHref ? (
                <a
                  href={card.descriptionHref}
                  className={styles.cardDescriptionLink}
                  data-testid={`reason-to-buy-card-${card.id}-link`}
                >
                  {card.description}
                </a>
              ) : card.onDescriptionClick ? (
                <UnderlinedButton
                  onClick={card.onDescriptionClick}
                  className={styles.cardDescriptionButton}
                  data-testid={`reason-to-buy-card-${card.id}-button`}
                  labelText={card.description}
                />
              ) : (
                <p className={styles.cardDescription}>{card.description}</p>
              ))}
          </li>
        ))}
      </ul>
      {totalPages > 1 && (
        <div
          className={styles.pagination}
          aria-hidden="true"
          data-testid="reason-to-buy-pagination"
        >
          {Array.from({ length: totalPages }).map((_, pageIndex) => (
            <span
              key={`pagination-dot-${pageIndex}`}
              className={styles.paginationDot}
              data-active={pageIndex === activePage}
            />
          ))}
        </div>
      )}
    </div>
  );
};
